/**
 * Gespreksregels (sessie 75, blok 2): wat de klant in het chatvenster of
 * op WhatsApp zag en deed, 1-op-1 in het gesprek. De server bewaart ze als
 * weergaveregel (`app/services/gespreksregels.py`):
 *
 * - `tegel`: de tegel die de klant koos op het startscherm;
 * - `gegevens`: wat de klant in het gegevenskaartje invulde;
 * - `kaart`: Nova's kaarten (track en trace, producten, keuzeknoppen,
 *   link, winkelwagen, voedingsresultaat, wijziging).
 *
 * Zelfde vorm als in het venster van de klant, in werkplekkleuren; geen
 * samenvatting en geen uitleg. Een keuzeknop die de klant koos, staat
 * gemarkeerd (het volgende klantbericht met precies die tekst).
 */
import type { V6Regel } from "../api";

type Data = Record<string, unknown>;

function tekst(waarde: unknown): string | null {
  return typeof waarde === "string" && waarde.trim() ? waarde.trim() : null;
}

function lijst(waarde: unknown): Data[] {
  return Array.isArray(waarde) ? waarde.filter((x): x is Data => typeof x === "object" && x !== null) : [];
}

function euro(cents: unknown): string | null {
  return typeof cents === "number"
    ? new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(cents / 100)
    : null;
}

function veiligeUrl(url: unknown): string | null {
  const t = tekst(url);
  if (!t) return null;
  try {
    const u = new URL(t);
    return u.protocol === "https:" || u.protocol === "http:" ? u.toString() : null;
  } catch {
    return null;
  }
}

const VOORRAAD: Record<string, string> = {
  op_voorraad: "Op voorraad",
  uitverkocht: "Tijdelijk uitverkocht",
};

const GEGEVENS_LABEL: Record<string, string> = {
  naam: "Naam",
  email: "E-mail",
  postcode: "Postcode",
  huisnummer: "Huisnummer",
  ordernummer: "Ordernummer",
};

function Tegel({ data }: { data: Data }) {
  const titel = tekst(data.titel) ?? `Tegel ${String(data.nummer ?? "")}`.trim();
  return (
    <div className="v6-regel tegel" data-testid="regel-tegel">
      <span className="knopje gekozen">{titel}</span>
    </div>
  );
}

function Gegevens({ data }: { data: Data }) {
  const velden = Object.keys(GEGEVENS_LABEL)
    .map((sleutel) => [GEGEVENS_LABEL[sleutel], tekst(data[sleutel])] as const)
    .filter(([, waarde]) => waarde);
  return (
    <div className="v6-regel gegevens" data-testid="regel-gegevens">
      <dl>
        {velden.map(([label, waarde]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{waarde}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Zending({ kaart }: { kaart: Data }) {
  return (
    <div className="kaartje zending">
      <b>{tekst(kaart.ordernummer) ?? "Zending"}</b>
      {lijst(kaart.pakketten).map((p, i) => {
        const url = veiligeUrl(p.track_trace_url);
        const regels = [
          tekst(p.vervoerder),
          tekst(p.status),
          tekst(p.verwachte_bezorging),
          tekst(p.verwacht_tijdvak),
          tekst(p.ophaalpunt),
        ].filter(Boolean);
        return (
          <div key={i} className="pakket">
            <span>{regels.join(", ")}</span>
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer">
                Track en trace
              </a>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Producten({ kaart }: { kaart: Data }) {
  return (
    <div className="kaartje producten">
      {tekst(kaart.kop) ? <b>{tekst(kaart.kop)}</b> : null}
      {lijst(kaart.producten).map((p, i) => {
        const url = veiligeUrl(p.url);
        const foto = veiligeUrl(p.afbeelding_url)?.startsWith("https:") ? veiligeUrl(p.afbeelding_url) : null;
        const voorraad = VOORRAAD[String(p.voorraad_status ?? "")] ?? null;
        const naam = [tekst(p.naam), tekst(p.variant)].filter(Boolean).join(", ");
        return (
          <div key={i} className="product">
            {foto ? <img src={foto} alt="" loading="lazy" /> : null}
            <span>
              {url ? (
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {naam}
                </a>
              ) : (
                naam
              )}
            </span>
            {voorraad ? <span className="v6-chip">{voorraad}</span> : null}
            {euro(p.prijs_cents) ? <span className="prijs num">{euro(p.prijs_cents)}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

function Keuzes({ kaart, gekozen }: { kaart: Data; gekozen: string | null }) {
  const opties = Array.isArray(kaart.opties) ? kaart.opties.map((o) => String(o)) : [];
  return (
    <div className="kaartje keuzes">
      {tekst(kaart.vraag) ? <p>{tekst(kaart.vraag)}</p> : null}
      <div className="knopjes">
        {opties.map((optie, i) => {
          const is = gekozen !== null && optie.trim().toLowerCase() === gekozen.trim().toLowerCase();
          return (
            <span key={i} className={`knopje${is ? " gekozen" : ""}`} aria-current={is ? "true" : undefined}>
              {optie}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function Link({ kaart }: { kaart: Data }) {
  const url = veiligeUrl(kaart.url);
  const label = tekst(kaart.label) ?? url ?? "Link";
  return (
    <div className="kaartje link">
      {url ? (
        <a className="knopje" href={url} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      ) : (
        <span className="knopje">{label}</span>
      )}
    </div>
  );
}

function Wagen({ kaart }: { kaart: Data }) {
  const wagen = veiligeUrl(kaart.winkelwagen_url);
  return (
    <div className="kaartje wagen">
      <b>{tekst(kaart.ordernummer) ? `Opnieuw bestellen ${tekst(kaart.ordernummer)}` : "Winkelwagen"}</b>
      {lijst(kaart.regels).map((r, i) => (
        <div key={i} className="product">
          <span>
            {`${typeof r.aantal === "number" ? `${r.aantal}x ` : ""}${[tekst(r.naam), tekst(r.variant)]
              .filter(Boolean)
              .join(", ")}`}
          </span>
          {tekst(r.melding) ? <span className="v6-chip">{tekst(r.melding)}</span> : null}
          {euro(r.prijs_cents) ? <span className="prijs num">{euro(r.prijs_cents)}</span> : null}
        </div>
      ))}
      {wagen ? (
        <a className="knopje" href={wagen} target="_blank" rel="noopener noreferrer">
          Naar de winkelwagen
        </a>
      ) : null}
    </div>
  );
}

function Voeding({ kaart }: { kaart: Data }) {
  const band =
    typeof kaart.gram_min === "number" && typeof kaart.gram_max === "number"
      ? ` (${kaart.gram_min} tot ${kaart.gram_max} g)`
      : "";
  const gram = typeof kaart.gram_per_dag === "number" ? `${kaart.gram_per_dag} g per dag${band}` : null;
  return (
    <div className="kaartje voeding">
      <b>{tekst(kaart.product) ?? "Voedingsadvies"}</b>
      {gram ? <span>{gram}</span> : null}
    </div>
  );
}

function Wijziging({ kaart }: { kaart: Data }) {
  return (
    <div className="kaartje wijziging">
      <b>{tekst(kaart.titel) ?? "Wijziging"}</b>
      {tekst(kaart.van) || tekst(kaart.naar) ? (
        <span>{[tekst(kaart.van), tekst(kaart.naar)].filter(Boolean).join(" naar ")}</span>
      ) : null}
    </div>
  );
}

function Kaart({ kaart, gekozen }: { kaart: Data; gekozen: string | null }) {
  switch (kaart.soort) {
    case "vo-zending":
      return <Zending kaart={kaart} />;
    case "vo-producten":
      return <Producten kaart={kaart} />;
    case "vo-keuzes":
      return <Keuzes kaart={kaart} gekozen={gekozen} />;
    case "vo-link":
      return <Link kaart={kaart} />;
    case "vo-wagen":
      return <Wagen kaart={kaart} />;
    case "vo-voedingsresultaat":
      return <Voeding kaart={kaart} />;
    case "vo-wijziging":
      return <Wijziging kaart={kaart} />;
    case "gegevenskaartje":
      return (
        <div className="kaartje">
          <b>Gegevenskaartje</b>
        </div>
      );
    case "vo-voedingsformulier":
      return (
        <div className="kaartje">
          <b>Voedingsformulier</b>
        </div>
      );
    default:
      return null;
  }
}

/** Eén weergaveregel. `gekozen`: de tekst van het klantbericht dat volgde. */
export function Gespreksregel({ regel, gekozen }: { regel: V6Regel; gekozen: string | null }) {
  const data = (regel.data ?? {}) as Data;
  switch (regel.soort) {
    case "tegel":
      return <Tegel data={data} />;
    case "gegevens":
      return <Gegevens data={data} />;
    case "kaart": {
      const kaarten = lijst(data.kaarten);
      if (kaarten.length === 0) return null;
      return (
        <div className="v6-regel kaarten ons" data-testid="regel-kaart">
          {kaarten.map((k, i) => (
            <Kaart key={i} kaart={k} gekozen={gekozen} />
          ))}
        </div>
      );
    }
    default:
      return null;
  }
}
